/**
 * Shared VariantBadge — the ONE component that renders the purchased
 * Missing-Sticker variant across every admin surface (quote list/detail,
 * order header, invoice, picking checklist, requested list).
 *
 * NEVER infers from sticker code, current price, or Master Sticker DB.
 * Reads only from persisted evidence provided by the shared
 * `variant_resolver` on the backend (payload.effective_variant +
 * payload.variant_conflict).
 */
import React from "react";

export const VARIANT_LABELS = {
  white:        "WHITE",
  blue:         "BLUE",
  gold_crumple: "GOLD CRUMPLE",
  red:          "RED",
  orange:       "ORANGE",
  purple:       "PURPLE",
};

// High-contrast tones. WHITE is intentionally NOT a pale pill — it's
// as visually obvious as BLUE / GOLD (P2 owner directive).
const TONE = {
  white:        "bg-white text-[var(--ink)] border-[var(--ink)]",
  blue:         "bg-blue-700 text-white border-[var(--ink)]",
  gold_crumple: "bg-amber-500 text-black border-[var(--ink)]",
  red:          "bg-red-700 text-white border-[var(--ink)]",
  orange:       "bg-orange-600 text-white border-[var(--ink)]",
  purple:       "bg-purple-700 text-white border-[var(--ink)]",
};

const SIZE = {
  sm:  "text-[10px] px-2 py-0.5",
  md:  "text-xs px-3 py-1",
  lg:  "text-sm px-4 py-1.5 font-black",
  xl:  "text-base px-5 py-2 font-black",
};

/**
 * <VariantBadge variant="blue" size="lg" />
 * <VariantBadge variant={null}          />         → UNKNOWN warning
 * <VariantBadge variant={null} conflict />         → CONFLICT warning
 */
export default function VariantBadge({
  variant, conflict = false, size = "md", withPrefix = false,
  className = "", testid,
}) {
  const s = SIZE[size] || SIZE.md;

  if (conflict) {
    return (
      <span
        data-testid={testid || "variant-badge-conflict"}
        className={`inline-flex items-center gap-1 border-2 rounded-full uppercase tracking-wider font-bold bg-red-700 text-white border-red-900 ${s} ${className}`}
        title="Persisted variant sources disagree — verify with customer before fulfilling."
      >
        <span aria-hidden>⚠</span> VARIANT CONFLICT
      </span>
    );
  }

  if (!variant || !VARIANT_LABELS[variant]) {
    return (
      <span
        data-testid={testid || "variant-badge-unknown"}
        className={`inline-flex items-center gap-1 border-2 rounded-full uppercase tracking-wider font-bold bg-amber-400 text-black border-[var(--ink)] ${s} ${className}`}
        title="No persisted variant on this order — verify with customer before fulfilling. Never default to WHITE."
      >
        <span aria-hidden>⚠</span> VARIANT UNKNOWN
      </span>
    );
  }

  return (
    <span
      data-testid={testid || `variant-badge-${variant}`}
      className={`inline-flex items-center gap-1 border-2 rounded-full uppercase tracking-wider font-bold ${TONE[variant]} ${s} ${className}`}
    >
      {withPrefix ? <span className="opacity-70">Variant ·</span> : null}
      {VARIANT_LABELS[variant]}
    </span>
  );
}
